import React from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Divider,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
} from "@mui/material";

import Client from "../Client";
import Config from "../ClientConfig";
import BackofficeReportsApi from "./backofficeReportsApi";

export default class InvoiceRequestCompact extends React.Component {
  constructor(props) {
    super(props);
    this.client = new Client();
    this.state = {
      // order lookup
      irefOrder: "",
      orderLoaded: false,
      orderPreview: null,

      // payload (hidden)
      order_iref: "",
      order_code: "",
      client_iref: "",
      counties: [],
      cities: [],

      // company (MANDATORY)
      company: {
        name: "",
        cuid: "",
        ruid: "",
        phone: "",
        email: "",
        address: {
          street: "",
          number: "",
          postalCode: "",
          city: {
            iref: "",
            name: "",
            county: {
              iref: "",
              name: "",
              country: {
                iref: "",
              },
            },
          },
        },
      },

      // UI
      msg: "",
      err: "",
      busy: false,
      isGenerating: false,

      // Return flow
      returnMode: false,
      returnPayType: 1, // 1 Cash, 2 Bank Transfer

      // result
      invoiceUrl: "",
      invoiceSerial: "",
      invoiceDate: "",
      invoiceOrderIref: "",
    };
  }

  loadInitialCounties = (countryIref) => {
    const data = this.client.getData(Config.API_URL_COUNTY, {
      country: countryIref,
      orderBy_Name: true,
    });
    this.setState({
      counties: Array.isArray(data?.counties)
        ? data.counties
        : Object.values(data?.counties || {}),
    });
  };

  componentDidMount() {
    this.loadInitialCounties("country061");
  }

  getOrder = (orderIref) => {
    const iref = parseInt(String(orderIref || ""), 10);
    if (!Number.isFinite(iref)) return null;

    const tries = [
      { url: Config.API_URL_ORDERS, params: { iref } },
      { url: Config.API_URL_ORDERS, params: { iref: iref } },
    ];

    const unwrap = (raw) => {
      const body = raw?.response || raw?.data || raw;
      if (!body || typeof body !== "object") return null;

      if (Array.isArray(body.orders) && body.orders[0]) return body.orders[0];
      if (Array.isArray(body?.data?.orders) && body.data.orders[0]) return body.data.orders[0];

      if (body.order && typeof body.order === "object") return body.order;
      if (body?.data?.order && typeof body.data.order === "object") return body.data.order;

      if (body.iref != null && (body.type != null || body.status != null)) return body;

      return null;
    };

    for (const t of tries) {
      try {
        const r = this.client.getData(t.url, t.params);
        const o = unwrap(r);
        if (o) return o;
      } catch {
        // keep trying
      }
    }
    return null;
  };

  pushJsonOrData = (url, payload) => {
    if (typeof this.client.pushJson === "function") return this.client.pushJson(url, payload);
    return this.client.pushData(url, payload);
  };

  parseCreatedOrder = (res) => {
    const body = res?.response || res?.data || res;
    const data = body?.data || body;
    const orderId = data?.order ?? data?.orderIref ?? data?.iref ?? null;
    const code = data?.code ?? null;
    const clientIref = data?.client ?? null;
    return {
      ok: !!orderId,
      orderId: orderId != null ? String(orderId) : "",
      code: code != null ? String(code) : "",
      clientIref: clientIref != null ? String(clientIref) : "",
    };
  };

  negateNum = (x) => {
    const n = Number(x);
    if (!Number.isFinite(n)) return x;
    return -n;
  };

  buildReturnOrderPayload = (order) => {
    const orderIref = order?.iref ?? this.state.order_iref ?? null;

    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const createdOn =
      `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
      `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    return {
      orderIref: Number(orderIref),
      payType: Number(this.state.returnPayType || 1),
      createdOn,
    };
  };

  // ---- helpers ----
  setIrefOrder = (e) => {
    const raw = e.target.value || "";
    const val = raw.replace(/\D/g, ""); // digits only
    this.setState({ irefOrder: val, err: "" });
  };

  setCompany = (k) => (e) =>
    this.setState((s) => ({ company: { ...s.company, [k]: e.target.value } }));

  setAddress = (k) => (e) =>
    this.setState((s) => ({
      company: { ...s.company, address: { ...s.company.address, [k]: e.target.value } },
    }));

  addCountry = (e) => {
    this.setState((s) => ({
      company: {
        ...s.company,
        address: {
          ...s.company.address,
          city: {
            ...s.company.address.city,
            iref: "",
            name: "",
            county: {
              ...s.company.address.city.county,
              iref: "",
              name: "",
              country: {
                ...s.company.address.city.county.country,
                iref: e,
              },
            },
          },
        },
      },
      cities: [],
    }));

    const data = this.client.getData(Config.API_URL_COUNTY, {
      country: e,
      orderBy_Name: true,
    });

    this.setState({
      counties: Array.isArray(data?.counties)
        ? data.counties
        : Object.values(data?.counties || {}),
    });
  };

  addCounty = (e) => {
    this.setState((s) => ({
      company: {
        ...s.company,
        address: {
          ...s.company.address,
          city: {
            ...s.company.address.city,
            iref: "",
            name: "",
            county: {
              ...s.company.address.city.county,
              iref: e,
              name:
                (s.counties || []).find((x) => String(x.iref) === String(e))?.name || "",
              country: {
                ...s.company.address.city.county.country,
              },
            },
          },
        },
      },
    }));

    const data = this.client.getData(Config.API_URL_CITY, {
      county: e,
      orderBy_Name: true,
    });

    this.setState({
      cities: Array.isArray(data?.cities)
        ? data.cities
        : Object.values(data?.cities || {}),
    });
  };

  addCity = (e) => {
    this.setState((s) => ({
      company: {
        ...s.company,
        address: {
          ...s.company.address,
          city: {
            ...s.company.address.city,
            iref: e,
            name: (s.cities || []).find((x) => String(x.iref) === String(e))?.name || "",
            county: {
              ...s.company.address.city.county,
            },
          },
        },
      },
    }));

    let cityData = this.client.getData(Config.API_URL_CITY + e, {});
    if (cityData) {
      this.setState((s) => ({
        company: {
          ...s.company,
          address: {
            ...s.company.address,
            postalCode:
              cityData.zipCode && parseInt(cityData.zipCode) !== 0
                ? cityData.zipCode
                : s.company.address.postalCode,
            city: {
              ...s.company.address.city,
              iref: e,
              name: cityData.name || s.company.address.city.name,
              county: {
                ...s.company.address.city.county,
                iref: cityData?.county?.iref || s.company.address.city.county.iref,
                name: cityData?.county?.name || s.company.address.city.county.name,
                country: {
                  ...s.company.address.city.county.country,
                  iref:
                    cityData?.county?.country?.iref ||
                    s.company.address.city.county.country.iref,
                },
              },
            },
          },
        },
      }));
    }
  };

  updateGeoData = (country_id, county_id, uCompany) => {
    let queryData = {
      country: country_id,
      orderBy_Name: true,
    };
    let data = this.client.getData(Config.API_URL_COUNTY, queryData);

    queryData = {
      county: county_id,
      orderBy_Name: true,
    };
    let citiesData = this.client.getData(Config.API_URL_CITY, queryData);

    this.setState({
      counties: Array.isArray(data?.counties)
        ? data.counties
        : Object.values(data?.counties || {}),
      cities: Array.isArray(citiesData?.cities)
        ? citiesData.cities
        : Object.values(citiesData?.cities || {}),
      company: uCompany,
      err: "",
      msg: "Company data imported.",
    });
  };

  // Build absolute media URL from relative/absolute path returned by backend
  toAbsoluteMediaUrl = (path) => {
    if (!path) return "";
    const s = String(path);
    if (/^https?:\/\//i.test(s)) return s;

    let origin = window.location.origin;
    try {
      const abs = new URL(this.client.prefix, window.location.origin);
      origin = abs.origin;
    } catch {}

    if (s.startsWith("/")) return origin + s;

    const mediaCfg = Config.MEDIA_URL || "/media/";
    if (/^https?:\/\//i.test(mediaCfg)) {
      return mediaCfg.replace(/\/+$/, "") + "/" + s.replace(/^\/+/, "");
    }
    const mediaBase = ("/" + mediaCfg.replace(/^\/+/, "")).replace(/\/+$/, "");
    return origin + mediaBase + "/" + s.replace(/^\/+/, "");
  };

  // ---- 1) Load order via reports v2 detail endpoint ----
  loadOrder = () => {
    const { irefOrder } = this.state;
    if (!irefOrder || !/^\d+$/.test(irefOrder)) {
      this.setState({ err: "Order iref must be numeric.", msg: "" });
      return;
    }

    const data = BackofficeReportsApi.getOrdersDetail(this.client, {
      iref: parseInt(irefOrder, 10)
    });

    if (!data || !data.result) {
      this.setState({ err: "Order not found.", msg: "", orderLoaded: false });
      return;
    }

    const r = data.result;
    const orderIref = r.order != null ? String(r.order) : "";
    const orderCode = r.code || "";
    const clientIref = r.clientIref != null ? String(r.clientIref) : "";

    if (!orderIref || !orderCode || !clientIref) {
      this.setState({
        err: "Missing order code / client iref in response.",
        msg: "",
        orderLoaded: false,
      });
      return;
    }

    this.setState({
      order_iref: orderIref,
      order_code: orderCode,
      client_iref: clientIref,
      orderLoaded: true,
      orderPreview: {
        code: r.code,
        clientName: r.clientName,
        location: r.location,
        date: r.createdOn ? new Date(r.createdOn).toLocaleString("ro-RO") : r.date,
        valueWVat: r.valueWVat,
        valueWoVat: r.valueWoVat,
        lines: r.lines,
      },
      err: "",
      msg: "Order loaded.",
      invoiceUrl: "",
      invoiceSerial: "",
      invoiceDate: "",
    });
  };

  // ---- 2) Lookup company by CUI ----
  lookupCompanyByCUI = () => {
    const cuid = (this.state.company.cuid || "").toString().trim();
    if (!cuid) {
      this.setState({ err: "Enter a CUI/CIF first.", msg: "" });
      return;
    }

    let data = null;
    try {
      if (typeof this.client.callExternalAPI === "function") {
        data = this.client.callExternalAPI(Config.API_URL_COMPANY_EXTAPI, { cui: cuid });
      } else {
        data = this.client.getData(Config.API_URL_COMPANY_EXTAPI, { cui: cuid });
      }
    } catch {
      this.setState({ err: "Company lookup failed.", msg: "" });
      return;
    }

    if (!data) {
      this.setState({ err: "No response from company service.", msg: "" });
      return;
    }

    const next = JSON.parse(JSON.stringify(this.state.company));
    try {
      if (data.cod === 200 && data.found && data.found.length > 0) {
        const comp = data.found[0];

        if (comp.date_generale) {
          next.name = comp.date_generale.denumire || next.name;
          next.ruid = comp.date_generale.nrRegCom || next.ruid;
          next.cuid = comp.date_generale.cui || next.cuid;
        }

        if (comp.adresa_sediu_social) {
          const adr = comp.adresa_sediu_social;
          if (adr.telefon) next.phone = adr.telefon;
          if (adr.scod_Postal) next.address.postalCode = adr.scod_Postal;

          if (adr.sdenumire_Strada) {
            let street = adr.sdenumire_Strada;
            const prefix = "Str. ";
            if (street.toLowerCase().startsWith(prefix.toLowerCase())) {
              street = street.slice(prefix.length);
            }
            next.address.street = street;
          }
          if (adr.snumar_Strada) next.address.number = adr.snumar_Strada;
        }

        if (data.geo) {
          if (!next.address) next.address = {};
          if (!next.address.city) next.address.city = {};
          if (!next.address.city.county) next.address.city.county = {};
          if (!next.address.city.county.country) next.address.city.county.country = {};

          next.address.city.iref = data.geo.city || "";
          next.address.city.county.iref = data.geo.county || "";
          next.address.city.county.country.iref = data.geo.country || "";

          this.updateGeoData(data.geo.country, data.geo.county, next);
          return;
        }

        this.setState({ company: next, err: "", msg: "Company data imported." });
      } else {
        this.setState({ err: "Company not found for the given CUI/CIF.", msg: "" });
      }
    } catch {
      this.setState({ err: "Unexpected response from company service.", msg: "" });
    }
  };

  // ---- 3) Generate invoice (and optionally: create return order first) ----
  _generateLock = false;

  generate = async () => {
    if (this._generateLock || this.state.busy) return;
    this._generateLock = true;

    const { orderLoaded, order_iref, order_code, client_iref, company, returnMode } = this.state;

    this.setState({ busy: true, err: "", msg: "", invoiceUrl: "", invoiceSerial: "", invoiceDate: "" });

    try {
      if (!orderLoaded || !order_iref || !/^\d+$/.test(String(order_iref))) {
        this.setState({ err: "Load a valid order first." });
        return;
      }

      if (
        !company?.name ||
        !company?.cuid ||
        !company?.address?.city?.iref ||
        !company?.address?.city?.county?.iref
      ) {
        this.setState({
          err: "Company name, CUI/CIF, County and City are required.",
        });
        return;
      }

      let targetOrderIref = order_iref;
      let targetOrderCode = order_code;
      let targetClientIref = client_iref;

      if (returnMode) {
        const full = await this.getOrder(order_iref);
        if (!full) {
          this.setState({ err: "Couldn't load order details from getOrder.", msg: "" });
          return;
        }

        const returnPayload = this.buildReturnOrderPayload(full);
        const createUrl = `${Config.BASE_API_URL}orders/returnOrder/`;

        const createRes = await this.pushJsonOrData(createUrl, returnPayload);
        const parsed = this.parseCreatedOrder(createRes);

        if (!parsed.ok) {
          const msg2 = (createRes && (createRes.message || createRes.detail)) || "Failed to create return order.";
          this.setState({ err: msg2, msg: "" });
          return;
        }

        targetOrderIref = parsed.orderId;
        const createdReturn = await this.getOrder(targetOrderIref);
        if (!createdReturn) {
          this.setState({
            err: `Return order created: ${targetOrderIref}, but it could not be reloaded for invoice generation.`,
            msg: "",
          });
          return;
        }

        targetOrderCode = createdReturn?.code || "";
        targetClientIref = String(createdReturn?.client?.iref ?? createdReturn?.client ?? "");

        this.setState({ msg: `Return order created: ${targetOrderIref}`, err: "" });
      }

      this.setState({ invoiceOrderIref: String(targetOrderIref || "") });

      const invoiceCompany = {
        ...company,
        address: {
          ...company.address,
          city: company.address.city?.iref || "",
        },
      };

      const payload = {
        order: {
          iref: parseInt(String(targetOrderIref), 10),
          code: targetOrderCode,
          client: targetClientIref,
          invoice: { company: invoiceCompany },
        },
      };

      const postUrl = Config.API_URL_ORDERS + "requestInvoice/";

      const res = await this.client.pushData(postUrl, payload);

      if (res && (res.status === "OK" || res.code === "ORDR-405")) {
        const serial = res.data && res.data.invoice;
        const rel = res.data && res.data.invoiceURL;
        const link = this.toAbsoluteMediaUrl(rel);

        if (link) {
          this.setState({
            msg: serial ? `Invoice: ${serial}` : "Invoice generated.",
            invoiceUrl: link,
            invoiceSerial: serial || "",
            invoiceDate: res.data?.documentDate || "",
            err: "",
          });
        } else {
          this.setState({ msg: "Invoice generated. Fetching link…", err: "" });
          await this.fetchInvoiceLink(targetOrderIref, returnMode);
        }
      } else {
        const msg = (res && (res.message || res.detail)) || "Failed to generate invoice.";
        this.setState({ err: msg });
      }
    } catch (e) {
      this.setState({ err: String(e?.message || e) });
    } finally {
      this.setState({ busy: false });
      this._generateLock = false;
    }
  };

  // ---- 4) Fetch invoice link (fallback GET) ----
  fetchInvoiceLink = async (orderIrefOverride, returnModeOverride) => {
    const order_id =
      orderIrefOverride ||
      this.state.invoiceOrderIref ||
      this.state.order_iref ||
      this.state.irefOrder;

    const returnMode =
      typeof returnModeOverride === "boolean" ? returnModeOverride : this.state.returnMode;

    if (!order_id || !/^\d+$/.test(String(order_id))) {
      this.setState({ err: "Order iref must be numeric.", msg: "" });
      return;
    }

    const orderIdInt = parseInt(String(order_id), 10);

    const unwrap = (raw) => raw?.response || raw?.data || raw;
    const getInvoiceObj = (raw) => {
      const b = unwrap(raw);
      return b?.invoice || b?.data?.invoice || null;
    };

    let res = null;
    let inv = null;

    try {
      res = await this.client.getData(Config.API_URL_ORDERS + "invoice/", { order_id: orderIdInt });
      inv = getInvoiceObj(res);
    } catch {
      // ignore; we'll possibly fallback below
    }

    if (!inv && returnMode) {
      try {
        const fallbackRes = await this.client.getData(`${Config.BASE_API_URL}orders/invoice/`, {
          order_id: orderIdInt,
        });
        const fallbackInv = getInvoiceObj(fallbackRes);
        if (fallbackInv) {
          res = fallbackRes;
          inv = fallbackInv;
        }
      } catch {
        // keep original res
      }
    }

    if (inv) {
      const link = this.toAbsoluteMediaUrl(inv.fileUrl || inv.url || inv.file || "");
      if (link) {
        this.setState({
          invoiceUrl: link,
          invoiceSerial: inv.serial || "",
          invoiceDate: inv.documentDate || "",
          msg: inv.serial ? `Invoice: ${inv.serial}` : "Invoice ready.",
          err: "",
        });
        return;
      }
    }

    const body = unwrap(res);
    const detail = body?.detail || body?.message;
    this.setState({ err: detail || "Invoice link not available yet.", msg: "" });
  };

  fieldSx = {
    "& .MuiInputBase-root": {
      height: 34,
      borderRadius: "12px",
      fontSize: {
        xs: "0.78rem",
        sm: "0.84rem",
      },
      bgcolor: "#fff",
    },
    // iOS zoom prevention: only the typed/selected value
    '& .MuiInputBase-input': {
      fontSize: {
        xs: '16px',
        sm: '0.84rem',
      },
    },
    // for Select native / MUI select value
    '& .MuiSelect-select': {
      fontSize: {
        xs: '16px',
        sm: '0.84rem',
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: {
        xs: "0.78rem",
        sm: "0.84rem",
      },
    },
  };

  render() {
    const {
      irefOrder,
      orderLoaded,
      orderPreview,
      company,
      returnMode,
      msg,
      err,
      busy,
      invoiceUrl,
      invoiceSerial,
      invoiceDate,
    } = this.state;

    return (
      <CardContent
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
          p: "0 !important",
          borderRadius: {
            xs: "14px",
            sm: "16px",
          },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            px: {
              xs: 0.75,
              sm: 1,
              md: 1.25,
            },
            py: {
              xs: 0.65,
              sm: 0.75,
            },
            flexShrink: 0,
            borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          }}
        >
          <Typography
            sx={{
              color: "#1a237e",
              fontWeight: 800,
              fontSize: {
                xs: "0.9rem",
                sm: "1rem",
                md: "1.05rem",
              },
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Create Invoice
          </Typography>

          <Typography
            sx={{
              mt: 0.15,
              color: "#64748b",
              fontWeight: 600,
              fontSize: {
                xs: "0.72rem",
                sm: "0.78rem",
              },
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            Load an order, complete company data and generate the invoice
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            px: {
              xs: 0.75,
              sm: 1,
              md: 1.25,
            },
            py: {
              xs: 0.75,
              sm: 1,
            },
          }}
        >
          {msg ? (
            <Alert
              severity="success"
              variant="outlined"
              sx={{
                mb: 1,
                py: 0.25,
                fontSize: "0.78rem",
                borderRadius: "12px",
              }}
            >
              {msg}
            </Alert>
          ) : null}

          {err ? (
            <Alert
              severity="error"
              variant="outlined"
              sx={{
                mb: 1,
                py: 0.25,
                fontSize: "0.78rem",
                borderRadius: "12px",
              }}
            >
              {err}
            </Alert>
          ) : null}

          <Card
            variant="outlined"
            sx={{
              borderRadius: "14px",
              borderColor: "rgba(15, 23, 42, 0.08)",
              boxShadow: "none",
              overflow: "hidden",
            }}
          >
            <CardContent
              sx={{
                p: {
                  xs: 1,
                  sm: 1.25,
                },
                "&:last-child": {
                  pb: {
                    xs: 1,
                    sm: 1.25,
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexWrap: "wrap",
                  mb: 1,
                }}
              >
                <TextField
                  size="small"
                  label="Order iref"
                  value={irefOrder}
                  onChange={this.setIrefOrder}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  sx={{
                    width: {
                      xs: "calc(100% - 90px)",
                      sm: 180,
                    },
                    flexGrow: {
                      xs: 1,
                      sm: 0,
                    },
                    ...this.fieldSx,
                  }}
                />

                <Button
                  size="small"
                  variant="outlined"
                  onClick={this.loadOrder}
                  disabled={!irefOrder || busy}
                  sx={{
                    height: 34,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 750,
                    flexShrink: 0,
                  }}
                >
                  Load
                </Button>
              </Box>

              {orderLoaded && orderPreview && (
                <Box
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: "12px",
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    bgcolor: "#f8fafc",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#111827",
                      fontWeight: 750,
                      fontSize: {
                        xs: "0.82rem",
                        sm: "0.9rem",
                      },
                      lineHeight: 1.35,
                      wordBreak: "break-word",
                    }}
                  >
                    {orderPreview.clientName} · {orderPreview.location}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#64748b",
                      fontWeight: 600,
                      fontSize: {
                        xs: "0.75rem",
                        sm: "0.82rem",
                      },
                      lineHeight: 1.35,
                      wordBreak: "break-word",
                    }}
                  >
                    Code: {orderPreview.code} · Order created: {orderPreview.date} · Lines: {orderPreview.lines}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#64748b",
                      fontWeight: 600,
                      fontSize: {
                        xs: "0.75rem",
                        sm: "0.82rem",
                      },
                      lineHeight: 1.35,
                    }}
                  >
                    Total w/VAT: {orderPreview.valueWVat} — wo/VAT: {orderPreview.valueWoVat}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              <Typography
                sx={{
                  color: "#1a237e",
                  fontWeight: 800,
                  fontSize: {
                    xs: "0.82rem",
                    sm: "0.9rem",
                  },
                  mb: 0.75,
                }}
              >
                Company details
              </Typography>

              <Grid container spacing={1}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Company name"
                    value={company.name}
                    onChange={this.setCompany("name")}
                    sx={this.fieldSx}
                  />
                </Grid>

                <Grid item xs={7} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="CUI/CIF"
                    value={company.cuid}
                    onChange={this.setCompany("cuid")}
                    sx={this.fieldSx}
                  />
                </Grid>

                <Grid item xs={5} sm={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={this.lookupCompanyByCUI}
                    disabled={!company.cuid || busy}
                    sx={{
                      height: 34,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 750,
                    }}
                  >
                    Lookup CUI
                  </Button>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reg. Com."
                    value={company.ruid}
                    onChange={this.setCompany("ruid")}
                    sx={this.fieldSx}
                  />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Phone"
                    value={company.phone}
                    onChange={this.setCompany("phone")}
                    sx={this.fieldSx}
                  />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email"
                    value={company.email}
                    onChange={this.setCompany("email")}
                    sx={this.fieldSx}
                  />
                </Grid>

                <Grid item xs={6} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="County"
                    value={company.address.city?.county?.iref || ""}
                    onChange={(e) => this.addCounty(e.target.value)}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    sx={this.fieldSx}
                  >
                    <option value=""> </option>
                    {(this.state.counties || []).map((county) => (
                      <option key={county.iref} value={county.iref}>
                        {county.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="City"
                    value={company.address.city?.iref || ""}
                    onChange={(e) => this.addCity(e.target.value)}
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    disabled={!company.address.city?.county?.iref}
                    sx={this.fieldSx}
                  >
                    <option value=""> </option>
                    {(this.state.cities || []).map((city) => (
                      <option key={city.iref} value={city.iref}>
                        {city.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={5} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Postal code"
                    value={company.address.postalCode}
                    onChange={this.setAddress("postalCode")}
                    sx={this.fieldSx}
                  />
                </Grid>

                <Grid item xs={7} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Street"
                    value={company.address.street}
                    onChange={this.setAddress("street")}
                    sx={this.fieldSx}
                  />
                </Grid>

                <Grid item xs={5} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Number"
                    value={company.address.number}
                    onChange={this.setAddress("number")}
                    sx={this.fieldSx}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 1 }} />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: {
                    xs: 1,
                    sm: 2,
                  },
                  flexWrap: "wrap",
                }}
              >
                <FormControlLabel
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: {
                        xs: "0.78rem",
                        sm: "0.86rem",
                      },
                      fontWeight: 650,
                      color: "#111827",
                    },
                  }}
                  control={
                    <Switch
                      checked={!!returnMode}
                      onChange={(e) => this.setState({ returnMode: !!e.target.checked })}
                      disabled={!orderLoaded || busy}
                      size="small"
                    />
                  }
                  label="Create RETURN order"
                />

                <TextField
                  select
                  size="small"
                  label="Return payment"
                  value={this.state.returnPayType}
                  onChange={(e) => this.setState({ returnPayType: Number(e.target.value) })}
                  SelectProps={{ native: true }}
                  disabled={!orderLoaded || busy || !returnMode}
                  sx={{
                    minWidth: {
                      xs: 150,
                      sm: 180,
                    },
                    ...this.fieldSx,
                  }}
                >
                  <option value={1}>Cash</option>
                  <option value={2}>Bank Transfer</option>
                </TextField>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 1.25,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => this.fetchInvoiceLink()}
                  disabled={!this.state.order_iref || busy}
                  sx={{
                    height: 34,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 750,
                  }}
                >
                  Check Link
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  onClick={this.generate}
                  disabled={!orderLoaded || busy}
                  sx={{
                    height: 34,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 750,
                    boxShadow: "none",
                    "&:hover": {
                      boxShadow: "none",
                    },
                  }}
                >
                  {busy ? "Generating..." : "Generate invoice"}
                </Button>
              </Box>

              {invoiceUrl ? (
                <Box sx={{ mt: 1 }}>
                  <Divider sx={{ my: 1 }} />
                  <Typography
                    sx={{
                      display: "block",
                      mb: 0.5,
                      color: "#64748b",
                      fontWeight: 650,
                      fontSize: {
                        xs: "0.75rem",
                        sm: "0.82rem",
                      },
                    }}
                  >
                    {invoiceSerial ? `Invoice: ${invoiceSerial}` : "Invoice file"}
                    {invoiceDate ? ` · Invoice date: ${invoiceDate}` : ""}
                  </Typography>

                  <Button
                    size="small"
                    variant="text"
                    component="a"
                    href={invoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      textTransform: "none",
                      fontWeight: 750,
                      borderRadius: "12px",
                    }}
                  >
                    Download PDF
                  </Button>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Box>
      </CardContent>
    );
  }
}
